import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import type { ValueGetter } from '@src/common/utils/form/ValueGetters';
import type { Order } from '@src/orders/domain/Order';
import type { PermitApplication } from '@src/orders/domain/PermitApplication';
import { isEntryValueEmpty } from '@src/orders/domain/PermitApplication';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { usePermitViewFieldLabels } from '@src/permitField/hooks/usePermitViewFieldLabels';
import { FieldRow } from '@src/permits/PermitSection';
import React from 'react';

interface SectionGroupProps {
  title: string;
  requiredFields: string[];
  subsections: {
    label: React.ReactNode;
    fields?: Array<string>;
    renderFields?: () => React.ReactNode;
    valueGetter?: ValueGetter;
  }[];
  permitApplication: PermitApplication;
  order: Order;
}

export const CategorySection = ({
  title,
  requiredFields,
  subsections,
  permitApplication,
  order,
}: SectionGroupProps) => {
  const hasContent = subsections.some(
    (s) => s.renderFields || (s.fields && Array.from(s.fields).length > 0),
  );
  if (!hasContent) return null;

  return (
    <Stack className={categoryFields}>
      <Typography variant="subtitle2" className={categoryTitle}>
        {title}
      </Typography>
      {subsections.map(({ label, fields, renderFields, valueGetter }, idx) => {
        const display = fields
          ? fields.some((x) => requiredFields.includes(x))
          : true;
        if (!display) return null;

        return (
          (renderFields || (fields && Array.from(fields).length > 0)) && (
            <React.Fragment key={idx}>
              <Stack>
                <Typography variant="body1" className={sectionTitle}>
                  {label}
                </Typography>
              </Stack>
              {renderFields
                ? renderFields()
                : fields &&
                  Array.from(fields)
                    .filter((field) => {
                      const value = permitApplication.getFieldValue(field);
                      return !isEntryValueEmpty(value);
                    })
                    .map((field) => {
                      return (
                        <FieldValue
                          key={field}
                          field={field}
                          requiredFields={requiredFields}
                          permitApplication={permitApplication}
                          order={order}
                          valueGetter={valueGetter}
                        />
                      );
                    })}
            </React.Fragment>
          )
        );
      })}
    </Stack>
  );
};
export const FieldValue = ({
  field,
  requiredFields,
  permitApplication,
  order,
  valueGetter,
}: FieldValueProps) => {
  const { formatField } = usePermitViewFieldLabels({ permitApplication });
  const rawValue = valueGetter
    ? valueGetter(field, permitApplication.entries)?.toString()
    : permitApplication.getFieldValue(field)?.toString();
  const { fieldLabel, value, valueUnit } = formatField({
    field,
    value: rawValue,
  });
  const lastUpdatedDate = order.getLastUpdatedFor(field, permitApplication.id);
  const lastUpdated = lastUpdatedDate
    ? new Timestamp(lastUpdatedDate).formatUTCDate()
    : undefined;
  return (
    <ConditionalField fields={requiredFields} conditions={field}>
      <FieldRow
        key={field}
        id={field}
        label={fieldLabel}
        value={value}
        lastUpdated={lastUpdated}
        valueUnit={valueUnit}
      />
    </ConditionalField>
  );
};

interface FieldValueProps {
  field: string;
  requiredFields?: string[];
  permitApplication: PermitApplication;
  order: Order;
  valueGetter?: ValueGetter;
}

const categoryTitle = css(() => ({
  borderBottom: '1px solid',
  borderColor: `${theme.palette.surface.outlineVariant}`,
  paddingBottom: `${theme.spacing['2']}`,
}));
const categoryFields = css(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
}));
const sectionTitle = css({
  fontWeight: '500',
  marginBottom: '6px',
  paddingBottom: `${theme.spacing['1']}`,
  paddingTop: `${theme.spacing['2']}`,
});
